import {useToast, useRole} from '@/hooks';
import {ProfileDetails, ProfileEditForm, SpecialistProfileEditForm, SpecialistProfileDetails} from '@/components';
import {getProfileRequest, getSpecialistProfileRequest, resetAuthMessage} from '@/services/redux/slices';
import {accountDeletionStatusSelector, authFailureStatus, authSuccessStatus} from '@/services/redux/selectors';
import {useDispatch, useSelector} from 'react-redux';
import React, {useEffect, useMemo, useState} from 'react';
import {ErrorCodes, SuccessCodes, getErrorMessage, getSuccessMessage} from '@/utils/getMessages';
import {resetMessage} from '@/utils/common_functions';
import {PatientAccountTicketRequestStatus} from '@/constants/enums';
import {useSearchParams} from 'react-router-dom';

export const UserProfileScreen: React.FC = () => {
  // ===========================
  // hooks
  // ===========================
  const dispatch = useDispatch();
  const {showToast} = useToast();
  const {isSpecialist} = useRole();
  const [searchParams, setSearchParams] = useSearchParams();
  const editModeFromUrl = searchParams.get('mode') === 'edit';
  const [editMode, setEditMode] = useState<boolean>(false);

  // ===========================
  // selectors
  // ===========================
  const profileFailure = useSelector(authFailureStatus) as ErrorCodes;
  const profileSuccess = useSelector(authSuccessStatus) as SuccessCodes;
  const accountDeletionStatus = useSelector(accountDeletionStatusSelector);

  // =======================================
  // states
  // =======================================
  const isEditing = useMemo(() => {
    // if account deletion status is approved for deletion, restrict editing access
    if (accountDeletionStatus === PatientAccountTicketRequestStatus.Approved) {
      return false;
    }
    return editMode;
  }, [editMode, accountDeletionStatus]);

  // =======================================
  // functions
  // =======================================
  function gotoViewMode() {
    setEditMode(false);
    setSearchParams({});
  }

  function gotoEditMode() {
    setEditMode(true);
    setSearchParams({mode: 'edit'});
  }

  // Use a ref to track if this is the initial mount
  const isInitialMount = React.useRef(true);

  useEffect(() => {
    // On initial mount, always start in read-only mode and clear URL params if present
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (editModeFromUrl) {
        setSearchParams({});
      }
      return;
    }
    // After initial mount, sync with URL params for navigation
    setEditMode(editModeFromUrl);
  }, [editModeFromUrl, setSearchParams]);

  // ===========================
  // side effect
  // ===========================

  useEffect(() => {
    // If account deletion is approved, show a warning toast and redirect to view mode
    if (isSpecialist) {
      dispatch(getSpecialistProfileRequest());
    } else {
      dispatch(getProfileRequest());
    }
  }, [isSpecialist]);

  useEffect(() => {
    if (profileFailure) {
      showToast(getErrorMessage(profileFailure), 'failure');
      resetMessage(() => dispatch(resetAuthMessage()));
    }
    if (profileSuccess) {
      if (['S-10049', 'S-10004'].includes(profileSuccess)) {
        // go back to profile screen after editing is done
        gotoViewMode();
        // Show success message
        showToast(getSuccessMessage(profileSuccess), 'success');
        // Reset message after a delay to allow toast to display
        setTimeout(() => {
          dispatch(resetAuthMessage());
        }, 100);
      } else if (profileSuccess === 'S-10048') {
        // Profile submitted for approval
        showToast(getSuccessMessage(profileSuccess), 'success');
        // Reset message after a delay to allow toast to display
        setTimeout(() => {
          dispatch(resetAuthMessage());
        }, 100);
      } else if (['S-10024', 'S-10030', 'S-10002', 'S-10031'].includes(profileSuccess)) {
        showToast(getSuccessMessage(profileSuccess), 'success');
        resetMessage(() => dispatch(resetAuthMessage()));
      }
    }
  }, [profileFailure, profileSuccess, dispatch, showToast]);

  return (
    <div className="gap-10 flex flex-col p-4 xl:p-11">
      {isEditing ? (
        isSpecialist ? (
          <SpecialistProfileEditForm onViewMode={gotoViewMode} />
        ) : (
          <ProfileEditForm onViewMode={gotoViewMode} />
        )
      ) : isSpecialist ? (
        <SpecialistProfileDetails onEdit={gotoEditMode} />
      ) : (
        <ProfileDetails onEdit={gotoEditMode} />
      )}
    </div>
  );
};
